/**
 * The workspace: everything one user owns, and the only thing the reducer
 * writes to.
 *
 * Shaped as normalized entity tables keyed by id, which is exactly what PLAN
 * §8.3 asks the client store to hold and what the per-user Durable Object will
 * mirror server-side. Records rather than arrays because every command
 * addresses one document by id, and because a `Record` gives O(1) lookup and
 * cheap structural sharing (one key replaced, every other value shared).
 */

import { DEFAULT_SOLVER_SALTS } from '@beerwright/core';
import { z } from 'zod';
import { WORKSPACE_SCHEMA_VERSION } from '../version';
import { batchDocSchema } from './batch';
import { blendDocSchema } from './blend';
import {
  acidChoiceIdSchema,
  currencyCodeSchema,
  DEFAULT_STOCKED_ACIDS,
  documentIdSchema,
  hopPackagingSchema,
  hopStoragePlaceSchema,
  positiveSchema,
  saltIdSchema,
} from './common';
import { customIngredientDocSchema } from './custom-ingredient';
import {
  BUILTIN_EQUIPMENT_PROFILES,
  DEFAULT_EQUIPMENT_PROFILE_ID,
  equipmentProfileDocSchema,
} from './equipment';
import { inventoryItemDocSchema } from './inventory';
import { journalDocSchema } from './journal';
import { miscProfileDocSchema } from './misc-profile';
import { DEFAULT_BATCH_VOLUME_L, DEFAULT_BOIL_TIME_MINUTES, recipeDocSchema } from './recipe';
import { recipeSnapshotDocSchema } from './recipe-snapshot';
import {
  BUILTIN_FERMENTATION_PROFILES,
  BUILTIN_MASH_PROFILES,
  fermentationProfileDocSchema,
  mashProfileDocSchema,
} from './schedule-profile';
import { sharedRecipeRefSchema } from './shared-recipe-ref';
import { BUILTIN_WATER_PROFILES, savedWaterProfileDocSchema } from './water-profile';

// ---------------------------------------------------------------------------
// Unit display preferences
// ---------------------------------------------------------------------------

/**
 * How each kind of measurement is *displayed*.
 *
 * Storage is always canonical (kg, L, °C, SG — PLAN §5); these preferences only
 * decide what the editor renders and what a typed `500g` is parsed against.
 * They are per measurement kind rather than one global switch because the
 * real-world request is mixed: American homebrewers routinely weigh grain in
 * pounds, hops in grams, and read a thermometer in °F (PLAN §3, "metric/US,
 * and mixed — e.g. kg + °F").
 */
export const unitPreferencesSchema = z.strictObject({
  /** Grain and other bulk masses: kg/g vs lb/oz. */
  mass: z.enum(['metric', 'us']),
  /** Hop masses, kept separate because g/oz splits differently from bulk grain. */
  hopMass: z.enum(['metric', 'us']),
  /** Batch, kettle and water volumes: L/mL vs gal/qt. */
  volume: z.enum(['metric', 'us']),
  /** Temperatures. */
  temperature: z.enum(['celsius', 'fahrenheit']),
  /** Gravity readout. */
  gravity: z.enum(['sg', 'plato', 'brix']),
  /** Colour readout. */
  color: z.enum(['srm', 'ebc']),
  /** Carbonation pressure readout. */
  pressure: z.enum(['psi', 'bar', 'kpa']),
});

/** @see unitPreferencesSchema */
export type UnitPreferences = z.infer<typeof unitPreferencesSchema>;

/** Which named system the preferences correspond to; `mixed` means "the user picked per kind". */
export const unitSystemSchema = z.enum(['metric', 'us', 'mixed']);

/** @see unitSystemSchema */
export type UnitSystem = z.infer<typeof unitSystemSchema>;

/**
 * How US grain masses are written in the editor.
 *
 * Kitchen scales often read pounds and ounces ("1 lb 8 oz"); brewing software
 * and some scales use decimal pounds ("1.5 lb"). Storage is always kilograms;
 * this only changes what the grist table shows and what a bare number means
 * alongside a typed `1 lb 8 oz`. It does not participate in
 * {@link unitSystemOf} — picking one or the other is not a mixed unit system.
 */
export const massFormatSchema = z.enum(['decimal', 'lbOz']);

/** @see massFormatSchema */
export type MassFormat = z.infer<typeof massFormatSchema>;

/** The fully-specified preferences each named system expands to. */
export const UNIT_SYSTEM_PRESETS: Readonly<Record<'metric' | 'us', UnitPreferences>> = {
  metric: {
    mass: 'metric',
    hopMass: 'metric',
    volume: 'metric',
    temperature: 'celsius',
    gravity: 'sg',
    color: 'ebc',
    pressure: 'bar',
  },
  us: {
    mass: 'us',
    hopMass: 'us',
    volume: 'us',
    temperature: 'fahrenheit',
    gravity: 'sg',
    color: 'srm',
    pressure: 'psi',
  },
};

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

/**
 * How long before a due addition we warn, in seconds. `0` is no warning.
 *
 * Enumerated rather than free numbers, and exported so the Settings row and the
 * Durable Object that schedules the pushes offer and accept the same five
 * choices (PLAN-BREW-ALERTS D-BA3).
 */
export const BREW_ALERT_LEAD_SECONDS = [0, 30, 60, 120, 300] as const;

/** How often an unanswered alert repeats, in seconds. `0` is once and done. */
export const BREW_ALERT_REPEAT_SECONDS = [0, 15, 30, 60] as const;

/** @see BREW_ALERT_LEAD_SECONDS */
export type BrewAlertLeadSeconds = (typeof BREW_ALERT_LEAD_SECONDS)[number];

/** @see BREW_ALERT_REPEAT_SECONDS */
export type BrewAlertRepeatSeconds = (typeof BREW_ALERT_REPEAT_SECONDS)[number];

/**
 * When brew day warns, and how insistently.
 *
 * A fixed choice on both numbers, never an open one: the server's alarm cadence
 * has to be bounded, and a workspace that could store `repeatSeconds: 1` can
 * ask a Durable Object to wake sixty times a minute.
 */
export const brewAlertsSchema = z.strictObject({
  /** Seconds of warning before each timer end and anchored addition. */
  leadSeconds: z.literal(BREW_ALERT_LEAD_SECONDS),
  /** Seconds between repeats until the step is ticked or quietened. */
  repeatSeconds: z.literal(BREW_ALERT_REPEAT_SECONDS),
});

/** @see brewAlertsSchema */
export type BrewAlerts = z.infer<typeof brewAlertsSchema>;

/** What a workspace gets before anybody visits Settings. @see brewAlertsSchema */
export const DEFAULT_BREW_ALERTS: BrewAlerts = { leadSeconds: 60, repeatSeconds: 30 };

/**
 * How many days before brew day a starter is built, as a workspace may set it.
 *
 * Bounded rather than open: one to five days is the whole range a starter is
 * ever built in, and a number outside it is a typo the calendar would go on
 * honouring for years.
 */
export const starterLeadDaysSchema = z.number().int().min(1).max(5);

/**
 * The lead a workspace gets before anybody visits Settings: **two days**.
 *
 * A stirred starter is at high krausen inside a day and has flocculated by the
 * second, which is what a brewer wants in the flask on brew morning: the yeast
 * dropped, the spent wort ready to decant. Building it the night before pitches
 * a flask still in full ferment; building it a week before spends the growth on
 * a starvation the cells then have to recover from.
 */
export const DEFAULT_STARTER_LEAD_DAYS = 2;

/** Workspace-wide settings. */
export const workspaceSettingsSchema = z.strictObject({
  /**
   * The named system, or `mixed`.
   *
   * Derived, not authoritative: {@link units} is always the full truth.
   * `settings.update` keeps the two consistent — setting `unitSystem` to
   * `metric`/`us` installs that preset, and changing an individual unit demotes
   * the system to `mixed` when it no longer matches any preset.
   */
  unitSystem: unitSystemSchema,
  /** The per-kind display preferences. Always fully specified. */
  units: unitPreferencesSchema,
  /**
   * Decimal pounds vs lb+oz for grain mass. Defaulted so a workspace saved
   * before this field existed still parses; ignored when {@link units.mass}
   * is metric.
   */
  massFormat: massFormatSchema.default('decimal'),
  /**
   * ISO 4217 code lot `unitCost`s are denominated in. Defaulted so a workspace
   * saved before costing still parses; changing it does **not** convert numbers
   * already on lots.
   */
  currency: currencyCodeSchema.default('USD'),
  /**
   * Where the brewer keeps hops, and in what (PLAN-HOP-AGE §E).
   *
   * A setting rather than a question on every lot: almost nobody keeps one
   * sack in the freezer and the next in a cupboard, so asking per lot is a
   * question answered identically forever. A lot that genuinely differs
   * overrides it through `InventoryItemDoc.hopStorage`.
   *
   * The default is `freezer` + `wrapped`, and the second half is deliberate.
   * `freezer` is where homebrew hops mostly live and is what the age dialog
   * has always defaulted to. `wrapped` rather than `nitrogen-flushed` because
   * a lot you are *tracking* is usually a pack you have opened, and core's own
   * note on that factor says an opened pouch is `wrapped` at best — guessing
   * the sealed figure would quietly flatter every lot in the cupboard.
   *
   * Defaulted, so a workspace saved before this parses; a new key that is not
   * required does not bump `WORKSPACE_SCHEMA_VERSION`.
   */
  hopStorageDefault: z
    .strictObject({ place: hopStoragePlaceSchema, packaging: hopPackagingSchema })
    .default({ place: 'freezer', packaging: 'wrapped' }),
  /** Equipment profile new recipes adopt; `null` falls back to the first built-in. */
  defaultEquipmentProfileId: documentIdSchema.nullable(),
  /**
   * The water profile "Set up water" starts a recipe from, **litres of nothing
   * assumed** when `null`.
   *
   * Deliberately weaker than {@link defaultEquipmentProfileId}, which a new
   * recipe adopts outright. A recipe's water is an embedded *copy*, not an id
   * reference, and a blank recipe carries `water: null` on purpose — the panel
   * says so ("we assume nothing about your water"). Seeding every new recipe
   * with a water section would reverse that. So this only changes what the
   * brewer gets when they ask for water: their own tap analysis instead of the
   * built-in RO the button used to hardcode.
   *
   * Defaulted, so a workspace saved before this parses — a new key that is not
   * required does not bump `WORKSPACE_SCHEMA_VERSION`; see that constant.
   */
  defaultWaterProfileId: documentIdSchema.nullable().default(null),
  /** Batch volume new recipes start at, **litres**. */
  defaultBatchVolumeL: positiveSchema.max(10_000),
  /** Boil length new recipes start at, **minutes**. */
  defaultBoilTimeMinutes: z.number().finite().min(0).max(1440),
  /**
   * Salts the water solver may suggest — the brewer's cupboard, not chemistry.
   *
   * Somebody who stocks Epsom salt but no gypsum should get a schedule they
   * can actually weigh out, so "Suggest additions" draws only from this list.
   * The default is core's {@link DEFAULT_SOLVER_SALTS} (no chalk, no slaked
   * lime, no anhydrous calcium chloride — the solver's header says why), and
   * the field is defaulted so a workspace saved before it existed still
   * parses. Never empty: a solver with nothing to reach for is a broken
   * button, not a preference.
   */
  solverSalts: z
    .array(saltIdSchema)
    .min(1)
    .default([...DEFAULT_SOLVER_SALTS]),
  /**
   * Acids the pickers offer — the same cupboard idea as {@link solverSalts},
   * one shelf along.
   *
   * Somebody who only ever buys phosphoric should not scroll past six lactic
   * and vinegar entries on every recipe, and should not have to re-pick their
   * own bottle each time: the mash and kettle pickers draw from this list and
   * open on the first of it. Stored as bottle-and-strength ids, not acids —
   * see {@link acidChoiceIdSchema}.
   *
   * Defaulted, so a workspace saved before it existed still parses, and never
   * empty: a picker with nothing in it cannot acidify anything.
   */
  stockedAcids: z
    .array(acidChoiceIdSchema)
    .min(1)
    .default([...DEFAULT_STOCKED_ACIDS]),
  /**
   * Brew-day warning and repeat intervals (PLAN-BREW-ALERTS D-BA3).
   *
   * Here rather than in `localStorage` because the Durable Object schedules the
   * background pushes from these same two numbers: a preference the server
   * cannot see would mean the phone in the pocket buzzing on a different
   * schedule from the tablet by the kettle, and a change on one device silently
   * not reaching the other.
   *
   * Defaulted, so a workspace saved before this parses — a new key that is not
   * required does not bump `WORKSPACE_SCHEMA_VERSION`.
   */
  brewAlerts: brewAlertsSchema.default(DEFAULT_BREW_ALERTS),
  /**
   * How many days before brew day **Starter** lands on the calendar, and the
   * reminder mail with it. @see DEFAULT_STARTER_LEAD_DAYS for why two.
   *
   * Defaulted, so a workspace saved before this parses — a new key that is not
   * required does not bump `WORKSPACE_SCHEMA_VERSION`.
   */
  starterLeadDays: starterLeadDaysSchema.default(DEFAULT_STARTER_LEAD_DAYS),
});

/** @see workspaceSettingsSchema */
export type WorkspaceSettings = z.infer<typeof workspaceSettingsSchema>;

/**
 * The settings a brand-new workspace starts with.
 *
 * US gallons / pounds / °F — the app is aimed at American homebrewers. Storage
 * stays canonical (litres, kilograms, °C); this is display only.
 */
export const DEFAULT_WORKSPACE_SETTINGS: WorkspaceSettings = {
  unitSystem: 'us',
  units: UNIT_SYSTEM_PRESETS.us,
  massFormat: 'decimal',
  currency: 'USD',
  // The freezer is where homebrew hops mostly live; `wrapped` rather than the
  // sealed-pouch figure because a lot being tracked is usually one you opened.
  hopStorageDefault: { place: 'freezer', packaging: 'wrapped' },
  defaultEquipmentProfileId: DEFAULT_EQUIPMENT_PROFILE_ID,
  // No preference until the brewer sets one; "Set up water" starts from RO.
  defaultWaterProfileId: null,
  defaultBatchVolumeL: DEFAULT_BATCH_VOLUME_L,
  defaultBoilTimeMinutes: DEFAULT_BOIL_TIME_MINUTES,
  solverSalts: [...DEFAULT_SOLVER_SALTS],
  stockedAcids: [...DEFAULT_STOCKED_ACIDS],
  // A minute of warning and a beep every thirty seconds: enough to weigh hops
  // out, insistent enough to carry from the next room.
  brewAlerts: DEFAULT_BREW_ALERTS,
  // Two days, so the flask has flocculated by brew morning and decants clear.
  starterLeadDays: DEFAULT_STARTER_LEAD_DAYS,
};

// ---------------------------------------------------------------------------
// The workspace
// ---------------------------------------------------------------------------

/** Everything one user owns. The reducer's only state. */
export const workspaceStateSchema = z.strictObject({
  /** Envelope schema version; see {@link WORKSPACE_SCHEMA_VERSION}. */
  schemaVersion: z.number().int().min(1),
  /** Recipes by id. */
  recipes: z.record(documentIdSchema, recipeDocSchema),
  /**
   * Named recipe versions by id — the snapshots half of version history.
   *
   * A sibling table rather than a list hanging off each recipe, for the reason
   * `batches` is one: a snapshot is a complete document that outlives the recipe
   * it was taken from. `.default({})` so a workspace saved before version
   * history still validates and simply has none (see
   * {@link WORKSPACE_SCHEMA_VERSION}).
   */
  recipeSnapshots: z.record(documentIdSchema, recipeSnapshotDocSchema).default({}),
  /**
   * Recipes shared with this workspace, by recipe id — pointers, not recipes
   * (PLAN-COLLAB A2).
   *
   * A sibling table rather than a flag on `recipes`, because a shared recipe is
   * not here at all: it lives in a room every member writes to, and what this
   * workspace keeps is only what is its own about it (role, joining time, an
   * equipment override). An id is in `recipes` or in here, never both.
   * `.default({})` so a workspace saved before sharing existed still validates
   * and simply has none (see {@link WORKSPACE_SCHEMA_VERSION}).
   */
  sharedRecipes: z.record(documentIdSchema, sharedRecipeRefSchema).default({}),
  /**
   * Batches by id — the brew log.
   *
   * A sibling table rather than a list hanging off each recipe, because a batch
   * outlives its recipe: `batch.recipeId` is nullable and every number a batch
   * reports comes from its own frozen snapshots. Deleting a recipe must not
   * take its history with it.
   */
  batches: z.record(documentIdSchema, batchDocSchema),
  /**
   * Blending sessions by id — beers that went into one blend.
   *
   * A sibling table rather than a list hanging off a batch: a blend is made
   * of several beers, some of which may not even be batches in this workspace.
   * `.default({})` so a workspace saved before blending sessions existed still
   * validates and simply has none (see {@link WORKSPACE_SCHEMA_VERSION}).
   */
  blends: z.record(documentIdSchema, blendDocSchema).default({}),
  /**
   * Journals by id — a page of blocks about one thing that is fermenting.
   *
   * A sibling table rather than a field on a batch, because a journal does not
   * need one: the mead with no recipe and the jar of kimchi are exactly who it
   * is for, and `journal.batchId` is a nullable pointer for the ones that do
   * (PLAN-JOURNAL D-J1). `.default({})` so a workspace saved before journals
   * existed still validates and simply has none (see
   * {@link WORKSPACE_SCHEMA_VERSION}).
   */
  journals: z.record(documentIdSchema, journalDocSchema).default({}),
  /**
   * Stocked ingredients by id — the cupboard (PLAN §3 V1).
   *
   * A flat table rather than four per-kind ones: every command addresses one
   * item by id, the availability check reads all four kinds together, and one
   * `Record` keeps structural sharing to a single replaced key.
   */
  inventory: z.record(documentIdSchema, inventoryItemDocSchema),
  /**
   * User-defined fermentables, hops, yeasts and miscs — the private library
   * PLAN §6 promised. Not the catalog: these live in the command log, per user,
   * and never ship as product data.
   *
   * `.default({})` so a workspace saved before this table existed still
   * validates and simply has none (see {@link WORKSPACE_SCHEMA_VERSION}).
   */
  customIngredients: z.record(documentIdSchema, customIngredientDocSchema).default({}),
  /** Equipment profiles by id, built-ins included. */
  equipmentProfiles: z.record(documentIdSchema, equipmentProfileDocSchema),
  /** Saved water profiles by id, built-ins included. */
  waterProfiles: z.record(documentIdSchema, savedWaterProfileDocSchema),
  /**
   * Saved mash and fermentation schedules (PLAN-PROFILES P1).
   *
   * `.default({})` so a workspace saved before these existed still validates,
   * which is exactly why {@link WORKSPACE_SCHEMA_VERSION} does not move for
   * them — see the rule stated on that constant. A bump would drop every
   * client's IndexedDB copy and force a full re-sync to gain nothing.
   */
  mashProfiles: z.record(documentIdSchema, mashProfileDocSchema).default({}),
  fermentationProfiles: z.record(documentIdSchema, fermentationProfileDocSchema).default({}),
  /**
   * Saved additions profiles (PLAN-ADDITIONS-PROFILES). Defaulted for the same
   * reason as the schedules above, and with no schema version bump either.
   * There are no built-ins, so `withShippedBuiltins` leaves it alone.
   */
  miscProfiles: z.record(documentIdSchema, miscProfileDocSchema).default({}),
  /** Workspace-wide settings. */
  settings: workspaceSettingsSchema,
});

/** @see workspaceStateSchema */
export type WorkspaceState = z.infer<typeof workspaceStateSchema>;

/**
 * A fresh workspace: no recipes, the built-in equipment and water profiles,
 * default settings.
 *
 * Pure and argument-free — two calls produce deep-equal values — so it is a
 * safe seed for both the client store and a newly-created Durable Object.
 */
export function emptyWorkspace(): WorkspaceState {
  return withShippedBuiltins({
    schemaVersion: WORKSPACE_SCHEMA_VERSION,
    recipes: {},
    recipeSnapshots: {},
    sharedRecipes: {},
    batches: {},
    blends: {},
    journals: {},
    inventory: {},
    customIngredients: {},
    equipmentProfiles: {},
    waterProfiles: {},
    mashProfiles: {},
    fermentationProfiles: {},
    miscProfiles: {},
    settings: { ...DEFAULT_WORKSPACE_SETTINGS, units: { ...DEFAULT_WORKSPACE_SETTINGS.units } },
  });
}

/**
 * Overlay every shipped built-in profile onto a workspace.
 *
 * Built-ins are product data, not user data: a later build that renames
 * "Generic 20 L all-grain" to "Generic 5 gal all-grain" must win over the
 * copy sitting in IndexedDB or the Durable Object. User-owned profiles
 * (`builtin: false`) are left alone. Missing shipped ids are added so a
 * workspace saved before a new built-in still lists it.
 */
export function withShippedBuiltins(state: WorkspaceState): WorkspaceState {
  const equipmentProfiles = { ...state.equipmentProfiles };
  for (const profile of BUILTIN_EQUIPMENT_PROFILES) {
    equipmentProfiles[profile.id] = { ...profile };
  }
  const waterProfiles = { ...state.waterProfiles };
  for (const profile of BUILTIN_WATER_PROFILES) {
    waterProfiles[profile.id] = { ...profile };
  }
  const mashProfiles = { ...state.mashProfiles };
  for (const profile of BUILTIN_MASH_PROFILES) {
    mashProfiles[profile.id] = { ...profile };
  }
  const fermentationProfiles = { ...state.fermentationProfiles };
  for (const profile of BUILTIN_FERMENTATION_PROFILES) {
    fermentationProfiles[profile.id] = { ...profile };
  }
  return { ...state, equipmentProfiles, waterProfiles, mashProfiles, fermentationProfiles };
}

/**
 * Which unit system a set of preferences corresponds to.
 *
 * @returns `metric` or `us` when the preferences match a preset exactly,
 *   otherwise `mixed`.
 */
export function unitSystemOf(units: UnitPreferences): UnitSystem {
  for (const system of ['metric', 'us'] as const) {
    const preset = UNIT_SYSTEM_PRESETS[system];
    const matches = (Object.keys(preset) as (keyof UnitPreferences)[]).every(
      (key) => preset[key] === units[key],
    );
    if (matches) return system;
  }
  return 'mixed';
}
